import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Gem, Palette, Scissors, Calendar } from 'lucide-react';

const benefits = [
  {
    id: 1,
    icon: Gem,
    title: 'Premium Fabrics',
    description: 'Sourced from the finest mills across India, our fabrics promise luxury and comfort.',
  },
  {
    id: 2,
    icon: Palette,
    title: 'Handpicked Designs',
    description: 'Each piece is carefully curated by our design experts to ensure timeless elegance.',
  },
  {
    id: 3,
    icon: Scissors,
    title: 'Elegant Craftsmanship',
    description: 'Traditional techniques meet modern precision in every stitch and embellishment.',
  },
  {
    id: 4,
    icon: Calendar,
    title: 'Perfect for Every Occasion',
    description: 'From weddings to festivals, find the perfect ensemble for your special moments.',
  },
];

const BenefitCard = ({ benefit, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });
  const Icon = benefit.icon;

  return (
    <motion.div
      ref={ref}
      className="relative p-8 md:p-10 text-center group"
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.15 }}
      data-testid={`benefit-card-${benefit.id}`}
    >
      {/* Background with hover effect */}
      <motion.div
        className="absolute inset-0 bg-white border border-[#E5E5E5]"
        whileHover={{
          borderColor: 'rgba(212, 175, 55, 0.5)',
          boxShadow: '0 10px 40px -10px rgba(128, 0, 32, 0.1)',
        }}
        transition={{ duration: 0.3 }}
      />

      {/* Content */}
      <div className="relative z-10">
        {/* Animated Icon */}
        <motion.div
          className="inline-flex items-center justify-center w-20 h-20 mb-6 bg-[#FDFBF7] border border-[#D4AF37]/30"
          whileHover={{ 
            scale: 1.1,
            borderColor: '#D4AF37',
            backgroundColor: '#800020',
          }}
          transition={{ duration: 0.3 }}
        >
          <motion.div
            whileHover={{ rotate: [0, -10, 10, 0] }}
            transition={{ duration: 0.5 }}
          >
            <Icon 
              className="text-[#800020] group-hover:text-white transition-colors duration-300" 
              size={32} 
            />
          </motion.div>
        </motion.div>

        {/* Title */}
        <h3 className="font-heading text-xl md:text-2xl text-[#1A1A1A] mb-4">
          {benefit.title}
        </h3>

        {/* Description */}
        <p className="font-body text-[#1A1A1A]/70 leading-relaxed">
          {benefit.description}
        </p>

        {/* Gold accent line */}
        <motion.div
          className="absolute bottom-0 left-1/2 transform -translate-x-1/2 h-0.5 bg-[#D4AF37]"
          initial={{ width: 0 }}
          whileHover={{ width: '60%' }}
          transition={{ duration: 0.3 }}
        />
      </div>
    </motion.div>
  );
};

export const CustomerExperience = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });

  return (
    <section
      id="ethnic-wear"
      ref={sectionRef}
      className="py-24 md:py-32 bg-[#F5F5F0]"
      data-testid="customer-experience-section"
    >
      <div className="max-w-[1920px] mx-auto px-4 md:px-8 lg:px-12">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <motion.span
            className="font-accent text-sm tracking-[0.3em] text-[#D4AF37] uppercase block mb-4"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ delay: 0.2 }}
          >
            Why Choose Us
          </motion.span>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl text-[#1A1A1A]">
            The Devanand Experience
          </h2>
          <motion.div
            className="w-24 h-0.5 bg-[#D4AF37] mx-auto mt-6"
            initial={{ scaleX: 0 }}
            animate={isInView ? { scaleX: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
          />
        </motion.div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {benefits.map((benefit, index) => (
            <BenefitCard key={benefit.id} benefit={benefit} index={index} />
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          className="text-center mt-16 p-12 bg-[#800020] relative overflow-hidden"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          {/* Decorative pattern */}
          <div className="absolute inset-0 opacity-10">
            <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
              <pattern id="diagonalHatch" patternUnits="userSpaceOnUse" width="10" height="10">
                <path d="M-1,1 l2,-2 M0,10 l10,-10 M9,11 l2,-2" stroke="white" strokeWidth="0.5" />
              </pattern>
              <rect fill="url(#diagonalHatch)" width="100%" height="100%" />
            </svg>
          </div>

          <div className="relative z-10">
            <h3 className="font-heading text-2xl md:text-3xl text-white mb-4">
              Experience Luxury, Embrace Tradition
            </h3>
            <p className="font-body text-white/80 mb-8 max-w-2xl mx-auto">
              Connect with us to explore our exclusive collection and receive personalized styling recommendations.
            </p>
            <motion.a
              href="https://wa.me/919535834444?text=Hi!%20I%20would%20like%20to%20know%20more%20about%20Devanand%20Vestment%20collections."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] text-black font-body text-sm tracking-widest uppercase hover:bg-white transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              data-testid="experience-whatsapp-btn"
            >
              Connect on WhatsApp
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
